import { rollup, sum } from "d3-array";
import { format } from "d3-format";
import { scaleLinear } from "d3-scale";
import { tuple } from "fp-ts/lib/function";
import { pipe } from "fp-ts/lib/pipeable";
import React from "react";
import { defineMessage, useIntl } from "react-intl";
import { removeTrailingZero } from "../charts/lib/utils";
import { StackedBarAuto } from "../charts-motion";
import { withFigureIO } from "../components/figure";
import { useReadScrolly } from "../components/scrolly";
import { metadata } from "../data/data_finance_aid_fig_EDU_SUB";
import { datasets } from "../data/data_finance_aid_fig_EDU_SUB_datasets";
import { mkGemDataDecoder, useButtonGroupState, useFigureState } from "../domain";
import { useMedia } from "../hooks";
import * as M from "../materials";
import { breakpoints } from "../materials/consts.stylex";
import { An, Ar, identity, io, O, Ord } from "../prelude";
export * from "../data/data_finance_aid_fig_EDU_SUB";
const Indicator = io.keyof({
    basic: null,
    secondary: null,
    tertiary: null,
    unspecified: null,
    projected: null
});
const indicatorLookup = {
    basic: defineMessage({
        id: "fig.finance.aid.EDU_SUB.basic",
        defaultMessage: "Basic education"
    }),
    secondary: defineMessage({
        id: "fig.finance.aid.EDU_SUB.secondary",
        defaultMessage: "Secondary education"
    }),
    tertiary: defineMessage({
        id: "fig.finance.aid.EDU_SUB.tertiary.FSHH",
        defaultMessage: "Tertiary education"
    }),
    unspecified: defineMessage({
        id: "fig.finance.aid.EDU_SUB.unspecified",
        defaultMessage: "Level unspecified"
    }),
    projected: defineMessage({
        id: "fig.finance.aid.EDU_SUB.projected",
        defaultMessage: "Projected"
    })
};
const indicatorLookupRel = {
    basic: defineMessage({
        id: "fig.finance.aid.EDU_SUB.basic",
        defaultMessage: "Basic education"
    }),
    secondary: defineMessage({
        id: "fig.finance.aid.EDU_SUB.secondary",
        defaultMessage: "Secondary education"
    }),
    tertiary: defineMessage({
        id: "fig.finance.aid.EDU_SUB.tertiary.FSHH",
        defaultMessage: "Tertiary education"
    }),
    unspecified: defineMessage({
        id: "fig.finance.aid.EDU_SUB.unspecified",
        defaultMessage: "Level unspecified"
    }),
    projected: defineMessage({
        id: "fig.finance.aid.EDU_SUB.projected",
        defaultMessage: "Projected"
    })
};
export const Data = mkGemDataDecoder([], {
    ind_id: Indicator,
    value: io.number,
    year: io.number
});
export const Chart = ({ data })=>{
    const intl = useIntl();
    const screenMDown = !useMedia(breakpoints.m);
    const [, actions] = useFigureState();
    const [selectedUnit] = useButtonGroupState("unit");
    const { activeSection } = useReadScrolly();
    const [scrollyUnit, selectedYears] = React.useMemo(()=>pipe(activeSection, O.map((x)=>x.split(",")), O.fold(()=>tuple("absolute", O.none), (strIds)=>{
            const [unit, ...years] = strIds; // Expected format: "relative,2011,2012,2013"
            const scrollyUnit = unit === "relative" ? "relative" : "absolute";
            return tuple(scrollyUnit, pipe(Ar.array.sequence(O.option)(years.map((d)=>O.some(d))), O.map((xs)=>xs), O.chain(An.fromArray)));
        })), [
        activeSection
    ]);
    const entityData = React.useMemo(()=>data.map((x)=>({
                ...x,
                indicator: intl.formatMessage(indicatorLookup[x.ind_id])
            })), [
        intl,
        data
    ]);
    React.useEffect(()=>{
        actions.setSelectionControl("unit", {
            type: "ButtonGroup",
            selected: "absolute"
        }, [
            {
                value: "absolute",
                label: intl.formatMessage(defineMessage({
                    id: "Absolute values",
                    defaultMessage: "Absolute values"
                }))
            },
            {
                value: "relative",
                label: intl.formatMessage(defineMessage({
                    id: "Ratio",
                    defaultMessage: "Ratio"
                }))
            }
        ]);
    }, [
        intl,
        actions
    ]);
    React.useEffect(()=>{
        if (O.isSome(activeSection)) {
            actions.updateSelectionControl("unit", scrollyUnit);
        }
    }, [
        intl,
        actions,
        activeSection,
        scrollyUnit
    ]);
    const isAbsolute = selectedUnit === "absolute";
    const stretchedScale = React.useMemo(()=>{
        const scales = rollup(entityData, (xs)=>scaleLinear().domain([
                0,
                sum(xs, (x)=>x.value)
            ]).range([
                0,
                1
            ]), (x)=>x.year);
        return (year, value)=>scales.get(year)(value);
    }, [
        entityData
    ]);
    const chartData = React.useMemo(()=>{
        if (isAbsolute) {
            return pipe(entityData, Ar.sortBy([
                Ord.contramap((x)=>x.year)(Ord.ordNumber)
            ]));
        }
        const mappedData = pipe(entityData, Ar.map((x)=>({
                ...x,
                value: stretchedScale(x.year, x.value)
            })));
        // filter out years that only have projected values > 0 and all other values are 0
        const yearGroups = rollup(mappedData, (xs)=>xs, (x)=>x.year);
        const yearsWithOnlyProjected = new Set(Array.from(yearGroups.entries()).filter(([_, entries])=>{
            const entriesWithValue = entries.filter((entry)=>entry.value > 0);
            const nonProjectedWithValue = entriesWithValue.filter((entry)=>entry.ind_id !== "projected");
            // Filter out if there are entries with value > 0, but all of them are projected
            return entriesWithValue.length > 0 && nonProjectedWithValue.length === 0;
        }).map(([year])=>year));
        return pipe(mappedData, Ar.filter((x)=>x.value > 0), Ar.filter((x)=>!yearsWithOnlyProjected.has(x.year)), Ar.sortBy([
            Ord.contramap((x)=>x.year)(Ord.ordNumber)
        ]));
    }, [
        isAbsolute,
        entityData,
        stretchedScale
    ]);
    const getStack = React.useCallback((x)=>`${x.year}`, []);
    const getLayer = React.useCallback((x)=>x.ind_id, []);
    const getLayerLabel = React.useCallback((ind_id)=>isAbsolute ? intl.formatMessage(indicatorLookup[ind_id]) : intl.formatMessage(indicatorLookupRel[ind_id]), [
        intl,
        isAbsolute
    ]);
    const getValue = React.useCallback((x)=>x.value || 0, []);
    const formatY = React.useMemo(()=>removeTrailingZero(format(isAbsolute ? ".1f" : ".0%")), [
        isAbsolute
    ]);
    const getColorPalette = React.useCallback(()=>M.colorRanges.discrete, []);
    return pipe(An.fromArray(chartData), O.map((xs)=><StackedBarAuto key={"linter-map"} height={screenMDown ? M.chartHeight.s : M.chartHeight.m} data={xs} getStack={getStack} getLayer={getLayer} getLayerLabel={getLayerLabel} getValue={getValue} formatX={identity} formatY={formatY} yUnitLabel={isAbsolute ? intl.formatMessage(defineMessage({
            id: "2022 constant US$ billion",
            defaultMessage: "2022 constant US$ billion"
        })) : ""} colorPalette={getColorPalette} showTooltip rotateXLabels={screenMDown} highlightedItems={selectedYears}/>), O.toNullable);
};
export default withFigureIO({
    url: new URL("../data/data_finance_aid_fig_EDU_SUB.json", import.meta.url).href,
    csv: new URL("../data/data_finance_aid_fig_EDU_SUB.zip", import.meta.url).href,
    xlsx: new URL("../data/data_finance_aid_fig_EDU_SUB.xlsx", import.meta.url).href,
    metadata,
    Data,
    Chart,
    size: "main",
    datasets
});
